import { Link, router } from '@inertiajs/react'
import { useState, useMemo } from 'react'
import { Layout } from '@/components/Layout'
import { PageHead } from '@/components/PageHead'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Plus, Pencil, Trash2, Server, ExternalLink, Search, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, X } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'

interface N8nInstance {
  id: string
  client_id: string
  client_name: string
  name: string
  base_url: string
  auth_type: string
  sync_status: 'ok' | 'error' | 'never'
  last_workflows_synced_at: string | null
  last_executions_synced_at: string | null
  last_sync_error: string | null
}

interface Client {
  id: string
  name: string
}

interface Props {
  instances: N8nInstance[]
  clients: Client[]
}

type SortKey = 'name' | 'client_name' | 'base_url' | 'sync_status' | 'last_synced'
type SortDirection = 'asc' | 'desc'
type SyncStatusFilter = 'all' | 'ok' | 'error' | 'never'

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Never'
  return new Date(dateString).toLocaleString()
}

function getLastSynced(instance: N8nInstance): Date | null {
  const dates = [instance.last_executions_synced_at, instance.last_workflows_synced_at]
    .filter(Boolean)
    .map(d => new Date(d!))
  if (dates.length === 0) return null
  return new Date(Math.max(...dates.map(d => d.getTime())))
}

function SyncStatusBadge({ status }: { status: N8nInstance['sync_status'] }) {
  switch (status) {
    case 'ok':
      return <Badge variant="success">Synced</Badge>
    case 'error':
      return <Badge variant="destructive">Error</Badge>
    case 'never':
    default:
      return <Badge variant="secondary">Never synced</Badge>
  }
}

export default function N8nInstancesIndex({ instances, clients }: Props) {
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)

  // Filter states
  const [clientFilter, setClientFilter] = useState<string | null>(null)
  const [syncStatusFilter, setSyncStatusFilter] = useState<SyncStatusFilter>('all')

  const handleClientFilterChange = (value: string | null) => {
    setClientFilter(value)
    setCurrentPage(1)
  }

  const handleSyncStatusFilterChange = (value: SyncStatusFilter) => {
    setSyncStatusFilter(value)
    setCurrentPage(1)
  }

  const clearFilters = () => {
    setClientFilter(null)
    setSyncStatusFilter('all')
    setSearch('')
    setCurrentPage(1)
  }

  const hasActiveFilters = clientFilter || syncStatusFilter !== 'all' || search.trim()

  // Filter
  const filteredInstances = useMemo(() => {
    let filtered = instances

    // Client filter
    if (clientFilter) {
      filtered = filtered.filter(i => i.client_id === clientFilter)
    }

    // Sync status filter
    if (syncStatusFilter !== 'all') {
      filtered = filtered.filter(i => i.sync_status === syncStatusFilter)
    }

    // Search filter
    if (search.trim()) {
      const searchLower = search.toLowerCase()
      filtered = filtered.filter(i =>
        i.name.toLowerCase().includes(searchLower) ||
        i.client_name.toLowerCase().includes(searchLower) ||
        i.base_url.toLowerCase().includes(searchLower)
      )
    }

    return filtered
  }, [instances, clientFilter, syncStatusFilter, search])

  // Sort
  const sortedInstances = useMemo(() => {
    if (!sortKey) return filteredInstances
    return [...filteredInstances].sort((a, b) => {
      let aVal: string | number | null
      let bVal: string | number | null

      if (sortKey === 'last_synced') {
        const aDate = getLastSynced(a)
        const bDate = getLastSynced(b)
        aVal = aDate ? aDate.getTime() : null
        bVal = bDate ? bDate.getTime() : null
      } else if (sortKey === 'sync_status') {
        const order = { ok: 0, error: 1, never: 2 }
        aVal = order[a.sync_status] ?? 3
        bVal = order[b.sync_status] ?? 3
      } else {
        aVal = a[sortKey]
        bVal = b[sortKey]
      }

      if (aVal == null && bVal == null) return 0
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }
      return sortDirection === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal))
    })
  }, [filteredInstances, sortKey, sortDirection])

  // Paginate
  const totalPages = Math.ceil(sortedInstances.length / itemsPerPage)
  const paginatedInstances = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    return sortedInstances.slice(start, start + itemsPerPage)
  }, [sortedInstances, currentPage, itemsPerPage])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  const handleSearch = (value: string) => {
    setSearch(value)
    setCurrentPage(1)
  }

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
    return sortDirection === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
  }

  const handleDelete = (id: string) => {
    router.delete(`/n8n_instances/${id}`)
  }

  return (
    <Layout>
      <PageHead title="n8n Instances" description="Manage n8n deployments and monitor sync status." />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">n8n Instances</h1>
            <p className="text-muted-foreground">
              Manage your n8n deployments and their sync status.
            </p>
          </div>
          <Button asChild>
            <Link href="/n8n_instances/new">
              <Plus className="mr-2 h-4 w-4" />
              Add Instance
            </Link>
          </Button>
        </div>

        {instances.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12">
            <Server className="h-12 w-12 text-muted-foreground" />
            <h2 className="mt-4 text-xl font-semibold">No instances yet</h2>
            <p className="mt-2 text-center text-muted-foreground">
              Add your first n8n instance to start tracking workflows.
            </p>
            <Button asChild className="mt-4">
              <Link href="/n8n_instances/new">
                <Plus className="mr-2 h-4 w-4" />
                Add Instance
              </Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search instances..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
              <Select
                value={clientFilter || 'all'}
                onValueChange={(value) => handleClientFilterChange(value === 'all' ? null : value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={syncStatusFilter}
                onValueChange={(value) => handleSyncStatusFilterChange(value as SyncStatusFilter)}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="ok">Synced</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                  <SelectItem value="never">Never Synced</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="mr-1 h-4 w-4" />
                  Clear filters
                </Button>
              )}
              {instances.length > 10 && (
                <div className="ml-auto">
                  <Select
                    value={String(itemsPerPage)}
                    onValueChange={(value) => {
                      setItemsPerPage(Number(value))
                      setCurrentPage(1)
                    }}
                  >
                    <SelectTrigger className="w-[130px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 per page</SelectItem>
                      <SelectItem value="25">25 per page</SelectItem>
                      <SelectItem value="50">50 per page</SelectItem>
                      <SelectItem value="100">100 per page</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button onClick={() => handleSort('name')} className="flex items-center font-medium hover:text-foreground">
                        Name <SortIcon column="name" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('client_name')} className="flex items-center font-medium hover:text-foreground">
                        Client <SortIcon column="client_name" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('base_url')} className="flex items-center font-medium hover:text-foreground">
                        URL <SortIcon column="base_url" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('sync_status')} className="flex items-center font-medium hover:text-foreground">
                        Sync Status <SortIcon column="sync_status" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('last_synced')} className="flex items-center font-medium hover:text-foreground">
                        Last Synced <SortIcon column="last_synced" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedInstances.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                        No instances match your search
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedInstances.map((instance) => (
                      <TableRow key={instance.id}>
                        <TableCell>
                          <Link
                            href={`/n8n_instances/${instance.id}`}
                            className="font-medium hover:underline"
                          >
                            {instance.name}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Link
                            href={`/clients/${instance.client_id}`}
                            className="hover:underline"
                          >
                            {instance.client_name}
                          </Link>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          <a
                            href={instance.base_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 hover:underline"
                          >
                            {instance.base_url}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </TableCell>
                        <TableCell>
                          <SyncStatusBadge status={instance.sync_status} />
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(instance.last_executions_synced_at || instance.last_workflows_synced_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="icon" asChild>
                              <Link href={`/n8n_instances/${instance.id}/edit`}>
                                <Pencil className="h-4 w-4" />
                                <span className="sr-only">Edit</span>
                              </Link>
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <Trash2 className="h-4 w-4" />
                                  <span className="sr-only">Delete</span>
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete instance?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete {instance.name} and all
                                    associated workflows and execution history. This
                                    action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(instance.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedInstances.length)} of {sortedInstances.length} instances
                  {search && ` (filtered from ${instances.length})`}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  )
}
